import React, { createContext, useContext, useState } from 'react';

export type PresetPeriod =
  | 'current-month'
  | 'last-3-months'
  | 'last-6-months'
  | 'current-year'
  | 'custom';

export interface PeriodState {
  preset: PresetPeriod;
  dateFrom: string; // ISO date YYYY-MM-DD
  dateTo: string;   // ISO date YYYY-MM-DD
}

export interface PeriodContextValue {
  period: PeriodState;
  setPeriod: (p: PeriodState) => void;
}

const SESSION_KEY = 'reports_period';

function getDefaultPeriod(): PeriodState {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return {
    preset: 'current-month',
    dateFrom: `${yyyy}-${mm}-01`,
    dateTo: `${yyyy}-${mm}-${dd}`,
  };
}

function loadFromSession(): PeriodState | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.preset && parsed.dateFrom && parsed.dateTo) {
      return parsed as PeriodState;
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

const PeriodContext = createContext<PeriodContextValue | null>(null);

export function PeriodProvider({ children }: { children: React.ReactNode }) {
  const [period, setPeriodState] = useState<PeriodState>(
    () => loadFromSession() ?? getDefaultPeriod()
  );

  const setPeriod = (p: PeriodState) => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(p));
    setPeriodState(p);
  };

  return (
    <PeriodContext.Provider value={{ period, setPeriod }}>
      {children}
    </PeriodContext.Provider>
  );
}

export function usePeriod(): PeriodContextValue {
  const ctx = useContext(PeriodContext);
  if (!ctx) {
    throw new Error('usePeriod must be used within a PeriodProvider');
  }
  return ctx;
}
